import { useEffect, useState } from 'react';
import {
  generatePath,
  useHistory,
  useParams,
  useLocation,
  useRouteMatch
} from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_POOL,
  ROUTE_PERSONS_ENTITY_PERSON
} from 'routes/RouteList';
import messages from 'messages';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper,
  RefactoredPanelHeader
} from 'components/panel';
import useTabIndex from 'hooks/utils/useTabIndex';
import PersonsTabs from 'containers/pages/persons/PersonsTabs';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';

const OverView = () => {
  const intl = useIntl();
  const ability = useAbility(AbilityContext);
  let location = useLocation();
  const { entityId, groupId, personId } = useParams();
  const { push } = useHistory();
  const active = useRouteMatch([
    ROUTE_PERSONS_ENTITY_POOL,
    ROUTE_PERSONS_ENTITY
  ]);

  const group = useEntityContext(s => s.group);
  const setGroup = useEntityContext(s => s.setGroup);
  const clearSelected = useEntityContext(s => s.clearSelected);

  const [tabIndex, setTabIndex] = useTabIndex(
    [ROUTE_PERSONS_ENTITY, ROUTE_PERSONS_ENTITY_POOL],
    false
  );

  const [panelTitle, setPanelTitle] = useState({ title: '', subTitle: '' });

  const isRootGroup = !groupId && groupId !== 'all' && entityId !== groupId;
  const isPool = active.path === ROUTE_PERSONS_ENTITY_POOL;

  useEffect(() => {
    return () => {
      clearSelected();
    };
  }, []);

  useEffect(() => {
    setPanelTitle({
      title: isRootGroup
        ? intl.formatMessage(messages.titleGroups)
        : isPool
          ? intl.formatMessage(messages.titlePool)
          : intl.formatMessage(messages.titleSporters, {
              inGroup: true,
              group: group?.name
            }),
      subTitle: intl.formatMessage(messages.subtitleSporters, {
        inGroup: !isRootGroup && !isPool,
        root: isRootGroup,
        group: group?.name
      })
    });
  }, [group]);

  useEffect(() => {
    if ((!groupId || groupId === 'all') && personId && tabIndex > 0) {
      setGroup({ id: entityId });
      setTabIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const onPersonClickHandler = (item, type) => {
    if (type === 'group') {
      push(generatePath(ROUTE_PERSONS_ENTITY, { entityId, groupId: item.id }));
    } else {
      if (item.id !== personId) {
        push(
          generatePath(ROUTE_PERSONS_ENTITY_PERSON, {
            entityId,
            groupId,
            personId: item.id
          })
        );
      }
    }
  };

  const onBreadcrumbClickedHandler = groupId => {
    push(
      generatePath(ROUTE_PERSONS_ENTITY, {
        entityId,
        groupId: entityId === groupId ? null : groupId
      })
    );
  };

  const onTabClickHandler = (tab, index) => {
    setTabIndex(index);
    if (tab === 'persons') {
      push(generatePath(ROUTE_PERSONS_ENTITY, { entityId }));
    }
    if (tab === 'pool') {
      setGroup({ id: entityId });
      push(
        generatePath(ROUTE_PERSONS_ENTITY, {
          entityId,
          groupId: 'all'
        })
      );
    }
  };

  return (
    <>
      <Panel active={active.isExact}>
        <RefactoredPanelHeader>
          <PanelTitleWrapper>
            <PanelTitle>{panelTitle.title}</PanelTitle>
            <PanelSubtitle>{panelTitle.subTitle}</PanelSubtitle>
          </PanelTitleWrapper>

          <PanelTitleButtonsGroup>
            <ActionButton />
            <ContextMenuWrapper trigger={<PanelTitleButton />}>
              <ContextMenu />
            </ContextMenuWrapper>
          </PanelTitleButtonsGroup>
        </RefactoredPanelHeader>

        <PanelBody>
          <PersonsTabs
            tabIndex={tabIndex}
            onClick={onPersonClickHandler}
            onBreadcrumbClicked={onBreadcrumbClickedHandler}
            onTabClick={onTabClickHandler}
            showPersonStatus={true}
            enableSelectAll={ability.can('manage', 'Persons')}
            checkable={ability.can('manage', 'Persons')}
            checkableGroups={tabIndex === 0 && ability.can('manage', 'Persons')}
          />
        </PanelBody>
      </Panel>
    </>
  );
};

export default OverView;
